import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Sparkles, Zap, X, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";

export interface BulkOperationProgress {
  isRunning: boolean;
  type: "prefill" | "generate" | "prefill-and-generate" | null;
  total: number;
  completed: number;
  currentBookTitle: string;
  errors: { bookId: number; title: string; error: string }[];
}

interface BulkActionToolbarProps {
  selectedCount: number;
  progress: BulkOperationProgress;
  onClearSelection: () => void;
  onBulkPrefill: () => void;
  onBulkGenerate: () => void;
  onBulkPrefillAndGenerate: () => void;
  onCancel: () => void;
}

const operationLabels: Record<string, string> = {
  prefill: "Pre-filling AI context",
  generate: "Generating embeddings",
  "prefill-and-generate": "Pre-fill + Generate",
};

const BulkActionToolbar = ({
  selectedCount,
  progress,
  onClearSelection,
  onBulkPrefill,
  onBulkGenerate,
  onBulkPrefillAndGenerate,
  onCancel,
}: BulkActionToolbarProps) => {
  const [showErrors, setShowErrors] = useState(false);

  const progressPercent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const isComplete = !progress.isRunning && progress.total > 0 && progress.completed > 0;
  const successCount = progress.completed - progress.errors.length;

  // Running state
  if (progress.isRunning) {
    return (
      <Card>
        <CardContent className="py-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">
                  {operationLabels[progress.type || ""] || "Processing"}...
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Processing {progress.completed} of {progress.total}
                  {progress.currentBookTitle && (
                    <> &mdash; {progress.currentBookTitle}</>
                  )}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={onCancel}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Completed state
  if (isComplete) {
    return (
      <Card>
        <CardContent className="py-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                <span className="font-medium text-green-700">{successCount} succeeded</span>
                {progress.errors.length > 0 && (
                  <>, <span className="font-medium text-red-700">{progress.errors.length} failed</span></>
                )}
              </p>
              <Button variant="ghost" size="sm" onClick={onClearSelection}>
                Dismiss
              </Button>
            </div>
            {progress.errors.length > 0 && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs p-0 h-auto text-red-700"
                  onClick={() => setShowErrors(!showErrors)}
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {showErrors ? "Hide" : "Show"} errors
                  {showErrors ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                </Button>
                {showErrors && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {progress.errors.map((err) => (
                      <p key={err.bookId} className="text-xs text-red-600">
                        <span className="font-medium">{err.title}:</span> {err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Idle state (items selected)
  if (selectedCount === 0) return null;

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {selectedCount} book{selectedCount !== 1 ? "s" : ""} selected
            </span>
            <Button variant="link" size="sm" className="text-xs p-0 h-auto" onClick={onClearSelection}>
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onBulkPrefill}>
              <FileText className="h-4 w-4 mr-1" />
              Pre-fill from Chapters
            </Button>
            <Button variant="outline" size="sm" onClick={onBulkGenerate}>
              <Sparkles className="h-4 w-4 mr-1" />
              Generate Embeddings
            </Button>
            <Button size="sm" onClick={onBulkPrefillAndGenerate}>
              <Zap className="h-4 w-4 mr-1" />
              Pre-fill + Generate
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkActionToolbar;
